import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '../components/common/Card';
import { Button } from '../components/common/Button';
import {
  Tabs,
  TabsContent,
} from '../components/common/Tabs';
import Layout from '../components/common/Layout';
import { useToast } from "../hooks/useToast";
import { Input } from '../components/common/Input';
import { changePassword, getProfile, updateProfile } from '../api/user';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/common/Dialog';
import { motion, AnimatePresence } from 'motion/react';
import PasswordCriteria from '../components/common/PasswordCriteria';

export default function ProfilePage() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [user, setUser] = useState({
    name: '',
    email: '',
    is_google_user: false
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profileData = await getProfile();
        setUser(profileData);
      } catch (error) {
        toast({
          title: "Error",
          description: error.message || 'Failed to fetch profile',
          type: "error"
        });
      }
    };

    fetchProfile();
  }, []);

  const isPasswordValid = () => {
    return passwordData.newPassword === passwordData.confirmPassword &&
           passwordData.currentPassword &&
           passwordData.newPassword &&
           passwordData.confirmPassword;
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await updateProfile(user.name);
      toast({
        title: "Success",
        description: "Profile updated successfully",
        type: "success"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to update profile',
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Error",
        description: "New passwords do not match",
        type: "error"
      });
      return;
    }

    setLoading(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast({
        title: "Success",
        description: "Password changed successfully",
        type: "success"
      });
      setShowChangePassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to change password',
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header Section */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Profile Settings</h1>
          <p className="text-muted-foreground mt-2">
            Manage your account settings and preferences
          </p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsContent value="profile" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>
                  Update your personal details and profile information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Full Name</label>
                    <Input
                      type="text"
                      value={user.name}
                      maxLength={50}
                      onChange={(e) => setUser({ ...user, name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md bg-background"
                      disabled={user.is_google_user}
                    />
                    {user.is_google_user && (
                      <p className="text-sm text-muted-foreground">Name cannot be changed for Google accounts</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email</label>
                    <Input
                      type="email"
                      disabled
                      value={user.email}
                      onChange={(e) => setUser({ ...user, email: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md bg-background"
                    />
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button
                  onClick={handleSaveProfile}
                  disabled={loading || user.is_google_user}
                  className="button-glow"
                >
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Account Security</CardTitle>
                <CardDescription>
                  Manage your password and account security settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  variant="outline"
                  onClick={() => setShowChangePassword(true)}
                  disabled={user.is_google_user}
                >
                  Change Password
                </Button>
                {user.is_google_user && (
                  <p className="text-sm text-muted-foreground">Password cannot be changed for Google accounts</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Change Password Dialog */}
        <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
              <DialogDescription>
                Enter your current password and choose a new one.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Current Password</label>
                <Input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ 
                    ...passwordData, 
                    currentPassword: e.target.value 
                  })}
                  placeholder="Enter your current password"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">New Password</label>
                <Input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ 
                    ...passwordData, 
                    newPassword: e.target.value 
                  })}
                  placeholder="Enter your new password"
                />
                <AnimatePresence>
                  {passwordData.newPassword && (
                    <div className="mt-2">
                      <PasswordCriteria password={passwordData.newPassword} />
                    </div>
                  )}
                </AnimatePresence>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Confirm New Password</label>
                <Input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ 
                    ...passwordData, 
                    confirmPassword: e.target.value 
                  })}
                  placeholder="Confirm your new password"
                />
                {passwordData.confirmPassword && passwordData.newPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                  <p className="text-sm text-destructive mt-1">Passwords do not match</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowChangePassword(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={loading || !isPasswordValid()}
                className="button-glow"
              >
                {loading ? "Changing..." : "Change Password"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
} 